import type { FlashcardData } from "@/components/flashcard";
import { createLogger } from "@/lib/logger";
import {
  ANKI_IMPORT_MAX_CARDS,
  type SessionVocabularyCard,
  getSessionVocabularyCardHash,
  hashCompact,
} from "@/lib/sessionVocabulary";
import { supabase } from "@/lib/supabaseClient";
import { useCallback, useEffect, useState } from "react";

const logger = createLogger("flashcards");
const ANKI_IMPORT_LOOKUP_BATCH_SIZE = 100;
const ANKI_IMPORT_INSERT_BATCH_SIZE = 200;

export type FlashcardType = "generated" | "imported";

export interface Flashcard {
  id: string;
  user_id: string;
  card_type?: FlashcardType | null;
  sentence: string;
  target_language: string;
  instruction_language: string;
  ipa_spelling: Record<string, string>;
  translation: string;
  example_sentences: Array<{
    sentence: string;
    translation: string;
  }>;
  grammar_points: Array<{
    title: string;
    explanation: string;
  }>;
  vocabulary: Array<{
    word: string;
    partOfSpeech: string;
    definition: string;
    usage?: string;
  }>;
  is_favorite: boolean;
  front_text?: string | null;
  back_text?: string | null;
  source?: string | null;
  source_deck_id?: string | null;
  source_deck_name?: string | null;
  source_card_hash?: string | null;
  source_tags?: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface ImportAnkiDeckParams {
  deckName: string;
  targetLanguage: string;
  instructionLanguage: string;
  cards: SessionVocabularyCard[];
  sourceDeckId?: string;
}

export interface ImportAnkiDeckResult {
  insertedCount: number;
  skippedCount: number;
  sourceDeckId: string;
  inputCount: number;
  limitedCount: number;
}

function chunkArray<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export function useFlashcards(userId?: string) {
  const [flashcards, setFlashcards] = useState<Flashcard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all flashcards for the current user
  const fetchFlashcards = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from("flashcards")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      setFlashcards(data || []);
    } catch (err) {
      setError("We couldn't load your flashcards. Check your connection and try again.");
      logger.error("Error fetching flashcards", {
        error: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Check if a specific sentence is already saved
  const isFlashcardSaved = useCallback(
    (sentence: string, targetLanguage: string): boolean => {
      return flashcards.some(
        (fc) => fc.sentence === sentence && fc.target_language === targetLanguage
      );
    },
    [flashcards]
  );

  // Check if a flashcard is favorited
  const isFlashcardFavorited = useCallback(
    (sentence: string, targetLanguage: string): boolean => {
      const flashcard = flashcards.find(
        (fc) => fc.sentence === sentence && fc.target_language === targetLanguage
      );
      return flashcard?.is_favorite || false;
    },
    [flashcards]
  );

  // Save a new flashcard
  const saveFlashcard = useCallback(
    async (
      sentence: string,
      targetLanguage: string,
      instructionLanguage: string,
      data: FlashcardData
    ): Promise<void> => {
      if (!userId) {
        throw new Error("User ID is required to save flashcards");
      }

      try {
        const { error: insertError } = await supabase.from("flashcards").insert({
          user_id: userId,
          card_type: "generated",
          sentence,
          target_language: targetLanguage,
          instruction_language: instructionLanguage,
          front_text: sentence,
          back_text: data.translation,
          ipa_spelling: data.ipaSpelling,
          translation: data.translation,
          example_sentences: data.exampleSentences,
          grammar_points: data.grammarPoints,
          vocabulary: data.vocabulary,
          is_favorite: false,
        });

        if (insertError) {
          // Handle unique constraint violation gracefully
          if (insertError.code === "23505") {
            throw new Error("This flashcard is already saved");
          }
          throw insertError;
        }

        // Refresh flashcards after saving
        await fetchFlashcards();
      } catch (err) {
        logger.error("Error saving flashcard", {
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    [userId, fetchFlashcards]
  );

  const importAnkiDeck = useCallback(
    async ({
      deckName,
      targetLanguage,
      instructionLanguage,
      cards,
      sourceDeckId,
    }: ImportAnkiDeckParams): Promise<ImportAnkiDeckResult> => {
      if (!userId) {
        throw new Error("User ID is required to import Anki cards");
      }

      const cleanDeckName = deckName.trim() || "Imported Anki deck";
      const cleanTargetLanguage = targetLanguage.trim();
      const cleanInstructionLanguage = instructionLanguage.trim() || "English";
      if (!cleanTargetLanguage) {
        throw new Error("Target language is required to import Anki cards");
      }

      const nextSourceDeckId =
        sourceDeckId || `anki:${hashCompact(`${userId}:${cleanTargetLanguage}:${cleanDeckName}`)}`;
      const uniqueCards = new Map<string, SessionVocabularyCard>();
      let validUniqueCardCount = 0;
      for (const card of cards) {
        const front = card.front.trim();
        const back = card.back.trim();
        if (!front || !back) continue;
        const hash = getSessionVocabularyCardHash({ ...card, front, back });
        if (uniqueCards.has(hash)) continue;
        validUniqueCardCount += 1;
        if (uniqueCards.size >= ANKI_IMPORT_MAX_CARDS) continue;
        uniqueCards.set(hash, {
          front,
          back,
          ...(card.tags?.length ? { tags: card.tags } : {}),
        });
      }

      if (!uniqueCards.size) {
        return {
          insertedCount: 0,
          skippedCount: 0,
          sourceDeckId: nextSourceDeckId,
          inputCount: cards.length,
          limitedCount: 0,
        };
      }

      const hashes = Array.from(uniqueCards.keys());
      const existingHashes = new Set<string>();
      for (const hashBatch of chunkArray(hashes, ANKI_IMPORT_LOOKUP_BATCH_SIZE)) {
        const { data: existingRows, error: existingError } = await supabase
          .from("flashcards")
          .select("source_card_hash")
          .eq("user_id", userId)
          .eq("card_type", "imported")
          .eq("target_language", cleanTargetLanguage)
          .eq("source_deck_id", nextSourceDeckId)
          .in("source_card_hash", hashBatch);

        if (existingError) throw existingError;

        for (const row of (existingRows || []) as Array<{ source_card_hash?: string | null }>) {
          if (row.source_card_hash) existingHashes.add(row.source_card_hash);
        }
      }

      const rows = Array.from(uniqueCards.entries())
        .filter(([hash]) => !existingHashes.has(hash))
        .map(([hash, card]) => ({
          user_id: userId,
          card_type: "imported",
          sentence: card.front,
          target_language: cleanTargetLanguage,
          instruction_language: cleanInstructionLanguage,
          ipa_spelling: {},
          translation: card.back,
          example_sentences: [],
          grammar_points: [],
          vocabulary: [],
          is_favorite: false,
          front_text: card.front,
          back_text: card.back,
          source: "anki",
          source_deck_id: nextSourceDeckId,
          source_deck_name: cleanDeckName,
          source_card_hash: hash,
          source_tags: card.tags || [],
        }));

      async function filterRowsForLegacySentenceConstraint(rowBatch: typeof rows) {
        const existingLegacySentences = new Set<string>();
        const fronts = rowBatch.map((row) => row.sentence);
        for (const frontBatch of chunkArray(fronts, ANKI_IMPORT_LOOKUP_BATCH_SIZE)) {
          const { data: existingRows, error: existingError } = await supabase
            .from("flashcards")
            .select("sentence")
            .eq("user_id", userId)
            .eq("target_language", cleanTargetLanguage)
            .in("sentence", frontBatch);

          if (existingError) throw existingError;

          for (const row of (existingRows || []) as Array<{ sentence?: string | null }>) {
            if (row.sentence) existingLegacySentences.add(row.sentence.toLocaleLowerCase());
          }
        }

        const seenLegacySentences = new Set<string>();
        return rowBatch.filter((row) => {
          const legacySentenceKey = row.sentence.toLocaleLowerCase();
          if (existingLegacySentences.has(legacySentenceKey)) return false;
          if (seenLegacySentences.has(legacySentenceKey)) return false;
          seenLegacySentences.add(legacySentenceKey);
          return true;
        });
      }

      let insertedCount = 0;
      if (rows.length) {
        for (const rowBatch of chunkArray(rows, ANKI_IMPORT_INSERT_BATCH_SIZE)) {
          const { error: insertError } = await supabase.from("flashcards").insert(rowBatch);
          if (!insertError) {
            insertedCount += rowBatch.length;
            continue;
          }

          if (insertError.code !== "23505") throw insertError;

          const legacySafeRows = await filterRowsForLegacySentenceConstraint(rowBatch);
          if (!legacySafeRows.length) continue;

          const { error: retryError } = await supabase.from("flashcards").insert(legacySafeRows);
          if (retryError) throw retryError;
          insertedCount += legacySafeRows.length;
        }
        if (insertedCount) await fetchFlashcards();
      }

      return {
        insertedCount,
        skippedCount: validUniqueCardCount - insertedCount,
        sourceDeckId: nextSourceDeckId,
        inputCount: cards.length,
        limitedCount: Math.max(0, validUniqueCardCount - ANKI_IMPORT_MAX_CARDS),
      };
    },
    [userId, fetchFlashcards]
  );

  // Toggle favorite status by stable row id. This matters for imported decks where
  // the same front text can appear in multiple decks.
  const toggleFavoriteById = useCallback(
    async (id: string): Promise<void> => {
      if (!userId) {
        throw new Error("User ID is required to toggle favorites");
      }

      try {
        const flashcard = flashcards.find((fc) => fc.id === id);

        if (!flashcard) {
          throw new Error("Flashcard not found");
        }

        const { error: updateError } = await supabase
          .from("flashcards")
          .update({ is_favorite: !flashcard.is_favorite })
          .eq("id", flashcard.id)
          .eq("user_id", userId);

        if (updateError) throw updateError;

        // Update local state
        setFlashcards((prev) =>
          prev.map((fc) => (fc.id === flashcard.id ? { ...fc, is_favorite: !fc.is_favorite } : fc))
        );
      } catch (err) {
        logger.error("Error toggling favorite", {
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    [userId, flashcards]
  );

  // Toggle favorite status for generated-card callers that still identify cards by sentence.
  const toggleFavorite = useCallback(
    async (sentence: string, targetLanguage: string): Promise<void> => {
      const flashcard = flashcards.find(
        (fc) => fc.sentence === sentence && fc.target_language === targetLanguage
      );

      if (!flashcard) {
        throw new Error("Flashcard not found");
      }

      await toggleFavoriteById(flashcard.id);
    },
    [flashcards, toggleFavoriteById]
  );

  // Delete a flashcard
  const deleteFlashcard = useCallback(
    async (id: string): Promise<void> => {
      if (!userId) {
        throw new Error("User ID is required to delete flashcards");
      }

      try {
        const { error: deleteError } = await supabase
          .from("flashcards")
          .delete()
          .eq("id", id)
          .eq("user_id", userId);

        if (deleteError) throw deleteError;

        // Update local state
        setFlashcards((prev) => prev.filter((fc) => fc.id !== id));
      } catch (err) {
        logger.error("Error deleting flashcard", {
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    [userId]
  );

  const deleteImportedFlashcards = useCallback(async (): Promise<number> => {
    if (!userId) {
      throw new Error("User ID is required to delete imported flashcards");
    }

    const importedCount = flashcards.filter((fc) => fc.card_type === "imported").length;

    try {
      const { error: deleteError } = await supabase
        .from("flashcards")
        .delete()
        .eq("user_id", userId)
        .eq("card_type", "imported");

      if (deleteError) throw deleteError;

      setFlashcards((prev) => prev.filter((fc) => fc.card_type !== "imported"));
      return importedCount;
    } catch (err) {
      logger.error("Error deleting imported flashcards", {
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  }, [userId, flashcards]);

  // Get flashcards by language
  const getFlashcardsByLanguage = useCallback(
    (targetLanguage: string): Flashcard[] => {
      return flashcards.filter((fc) => fc.target_language === targetLanguage);
    },
    [flashcards]
  );

  // Get favorite flashcards
  const getFavoriteFlashcards = useCallback((): Flashcard[] => {
    return flashcards.filter((fc) => fc.is_favorite);
  }, [flashcards]);

  // Fetch flashcards on mount or when userId changes
  useEffect(() => {
    if (userId) {
      fetchFlashcards();
    }
  }, [userId, fetchFlashcards]);

  return {
    flashcards,
    loading,
    error,
    fetchFlashcards,
    isFlashcardSaved,
    isFlashcardFavorited,
    saveFlashcard,
    importAnkiDeck,
    toggleFavoriteById,
    toggleFavorite,
    deleteFlashcard,
    deleteImportedFlashcards,
    getFlashcardsByLanguage,
    getFavoriteFlashcards,
  };
}
